/**
 * @file OrgLevelsTab.js
 * @description Component for rendering organization levels tab.
 **/
import React, { useState, useEffect } from "react";
import DepartmentMultiSelect from "../shared/asyncSearchableMultiSelect/DepartmentMultiSelect";
import DivisionMultiSelect from "../shared/asyncSearchableMultiSelect/DivisionMultiSelect";
import RegionMultiSelect from "../shared/asyncSearchableMultiSelect/RegionMultiSelect";
import LocationMultiSelect from "../shared/asyncSearchableMultiSelect/LocationMultiSelect";
import BusinessUnitMultiSelect from "../shared/asyncSearchableMultiSelect/BusinessUnitMultiSelect";
import PayGroupMultiSelect from "../shared/asyncSearchableMultiSelect/PayGroupMultiSelect";

/**
 * Represents a tab component for managing organizational levels.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.data - The data for the organizational levels.
 * @param {Function} props.onChange - The callback function to handle changes in the organizational levels.
 * @returns {JSX.Element} The rendered component.
 */
const OrgLevelsTab = ({ data, onChange }) => {
  const [formData, setFormData] = useState(data);

  useEffect(() => {
    const updatedFormData = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (value.length > 0) {
        updatedFormData[key] = value;
      }
    });
    onChange(updatedFormData);
  }, [formData, onChange]);

  const handleMultiSelectChange = (name, selectedValues) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: selectedValues,
    }));
  };

  return (
    <div className="col-12 col-xs-12 col-md-12 col-lg-6 m-auto mb-5">
      <div className="form-group">
        <DepartmentMultiSelect
          className="mb-4"
          tenantId={data.tenantId}
          selectedDepartments={formData.departments || []}
          onDepartmentsChange={(selectedDepartments) =>
            handleMultiSelectChange("departments", selectedDepartments)
          }
          showSelectionBox={false}
          showSelectedCount={true}
        />
      </div>
      <div className="form-group">
        <DivisionMultiSelect
          className="mb-4"
          tenantId={data.tenantId}
          selectedDivisions={formData.divisions || []}
          onDivisionsChange={(selectedDivisions) =>
            handleMultiSelectChange("divisions", selectedDivisions)
          }
          showSelectionBox={false}
          showSelectedCount={true}
        />
      </div>
      <div className="form-group">
        <RegionMultiSelect
          className="mb-4"
          tenantId={data.tenantId}
          selectedRegions={formData.regions || []}
          onRegionsChange={(selectedRegions) =>
            handleMultiSelectChange("regions", selectedRegions)
          }
          showSelectionBox={false}
          showSelectedCount={true}
        />
      </div>
      <div className="form-group">
        <LocationMultiSelect
          className="mb-4"
          tenantId={data.tenantId}
          selectedLocations={formData.locations || []}
          onLocationsChange={(selectedLocations) =>
            handleMultiSelectChange("locations", selectedLocations)
          }
          showSelectionBox={false}
          showSelectedCount={true}
        />
      </div>
      <div className="form-group">
        <BusinessUnitMultiSelect
          className="mb-4"
          tenantId={data.tenantId}
          selectedBusinessUnits={formData.businessUnits || []}
          onBusinessUnitsChange={(selectedBusinessUnits) =>
            handleMultiSelectChange("businessUnits", selectedBusinessUnits)
          }
          showSelectionBox={false}
          showSelectedCount={true}
        />
      </div>
      <div className="form-group">
        <PayGroupMultiSelect
          className="mb-4"
          tenantId={data.tenantId}
          selectedPayGroups={formData.payGroups || []}
          onPayGroupsChange={(selectedPayGroups) =>
            handleMultiSelectChange("payGroups", selectedPayGroups)
          }
          showSelectionBox={false}
          showSelectedCount={true}
        />
      </div>
    </div>
  );
};

export default OrgLevelsTab;